import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FiAlertCircle,
  FiArrowRight,
  FiCalendar,
  FiCheckCircle,
  FiClock,
  FiFileText,
  FiInfo,
  FiLoader,
  FiPlayCircle,
  FiRefreshCw,
  FiSearch,
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import '../scss/JobList.scss';
import http, { getUserFacingError } from '../services/http';
import { interviewService } from '../services/interviewService';
import { readAuthUserId } from '../services/sessionStorage';
import { reconcileInterviewTechnicalInterruptionIntents } from '../utils/interviewTechnicalInterruption';
import { resolveInterviewTimestampMeta } from '../utils/interviewTimestamp';

type InterviewStatusFilter = 'all' | 'not_started' | 'in_progress' | 'technical_interrupted' | 'completed';

const StatusLamaran: React.FC = () => {
  const navigate = useNavigate();
  const [myApplications, setMyApplications] = useState<any[]>([]);
  const [myInterviews, setMyInterviews] = useState<any[]>([]);
  const [applicationsLoading, setApplicationsLoading] = useState(true);
  const [applicationsNotice, setApplicationsNotice] = useState<string | null>(null);
  const [applicationInterviewFilter, setApplicationInterviewFilter] = useState<InterviewStatusFilter>('all');
  const [applicationStatusFilter, setApplicationStatusFilter] = useState('all');
  const [applicationSearch, setApplicationSearch] = useState('');

  const loadApplications = useCallback(async () => {
    setApplicationsLoading(true);
    setApplicationsNotice(null);

    const userId = readAuthUserId();
    if (userId) {
      await reconcileInterviewTechnicalInterruptionIntents(
        userId,
        (intent) => interviewService.technicalInterruption(
          intent.interviewId,
          { reason_code: intent.reasonCode, checkpoint: intent.checkpoint },
        ),
      );
    }

    return Promise.allSettled([http.get<{ status: boolean; data: any[] }>('auth/my-applications'), interviewService.list()])
      .then(([applicationResult, interviewResult]) => {
        if (applicationResult.status === 'fulfilled') {
          setMyApplications(applicationResult.value.data?.data || []);
        } else {
          setMyApplications([]);
          setApplicationsNotice(
            getUserFacingError(applicationResult.reason, 'Status lamaran belum bisa dimuat saat ini.').message
          );
        }

        if (interviewResult.status === 'fulfilled') {
          setMyInterviews(Array.isArray(interviewResult.value.data?.data) ? interviewResult.value.data.data : []);
        } else {
          setMyInterviews([]);
          setApplicationsNotice((current) => current || 'Status interview belum bisa dimuat saat ini.');
        }
      })
      .finally(() => setApplicationsLoading(false));
  }, []);

  useEffect(() => {
    void loadApplications();
  }, [loadApplications]);

  const getApplicationInterview = useCallback(
    (application: any) => myInterviews
      .filter(
        (interview: any) => interview.outsource_id === application.outsource_id && interview.job_id === application.job?.id
      )
      .sort((left: any, right: any) => Number(right.id || 0) - Number(left.id || 0))[0],
    [myInterviews]
  );

  const getInterviewFlowState = useCallback((interview: any): InterviewStatusFilter => {
    if (!interview) return 'not_started';
    if (interview.status === 'in_progress') return 'in_progress';
    if (interview.status === 'technical_interrupted') return 'technical_interrupted';
    if (interview.status === 'completed') return 'completed';
    return 'not_started';
  }, []);

  const applicationStatusOptions = useMemo(() => {
    const names = myApplications
      .map((application: any) => application.status?.ms_status_name)
      .filter((name: string) => Boolean(name) && name.trim().toLowerCase() !== 'new registrant');
    return Array.from(new Set(names));
  }, [myApplications]);

  const filteredApplications = useMemo(() => {
    const search = applicationSearch.trim().toLowerCase();

    return myApplications.filter((application: any) => {
      const interview = getApplicationInterview(application);
      const interviewFlowState = getInterviewFlowState(interview);
      const statusName = application.status?.ms_status_name || 'Menunggu Review';

      if (applicationInterviewFilter !== 'all' && interviewFlowState !== applicationInterviewFilter) return false;
      if (applicationStatusFilter !== 'all' && statusName !== applicationStatusFilter) return false;

      if (search) {
        const searchableText = [
          application.job?.title,
          application.position,
          application.job?.company?.name,
          application.job?.company?.company_name,
          application.company?.name,
          application.company?.company_name,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        if (!searchableText.includes(search)) return false;
      }

      return true;
    });
  }, [
    applicationInterviewFilter,
    applicationSearch,
    applicationStatusFilter,
    getApplicationInterview,
    getInterviewFlowState,
    myApplications,
  ]);

  const statusMeta = (name: string = '') => {
    const normalized = name.toLowerCase();

    if (normalized.includes('terima') || normalized.includes('approve')) {
      return { tone: 'success', label: name || 'Diterima', icon: <FiCheckCircle /> };
    }

    if (normalized.includes('wawancara') || normalized.includes('interview')) {
      return { tone: 'info', label: name || 'Interview', icon: <FiPlayCircle /> };
    }

    if (normalized.includes('tolak') || normalized.includes('reject')) {
      return { tone: 'danger', label: name || 'Ditolak', icon: <FiAlertCircle /> };
    }

    return { tone: 'warning', label: name || 'Menunggu Review', icon: <FiClock /> };
  };

  const resetApplicationFilters = () => {
    setApplicationInterviewFilter('all');
    setApplicationStatusFilter('all');
    setApplicationSearch('');
  };

  const hasAdditionalFilters =
    applicationInterviewFilter !== 'all' ||
    applicationStatusFilter !== 'all' ||
    applicationSearch.trim().length > 0;

  return (
    <div className="jl-bg">
      <div className="jl-container">
        <div className="jl-page-shell jl-page-shell--status">
          {applicationsNotice && !applicationsLoading && (
            <div className="jl-inline-notice">
              <FiInfo />
              <p>{applicationsNotice}</p>
            </div>
          )}

          {applicationsLoading ? (
            <div className="jl-state jl-state--loading">
              <FiLoader className="spin" />
              <p>Memuat status lamaran...</p>
            </div>
          ) : myApplications.length === 0 ? (
            <div className="jl-state jl-state--empty">
              <FiFileText />
              <p>Belum ada lamaran yang tersimpan.</p>
              <p className="jl-state__sub">Mulai dari halaman lowongan kerja untuk melamar posisi yang kamu incar.</p>
              <button type="button" onClick={() => navigate('/lowongan-kerja')}>
                Buka lowongan kerja
              </button>
            </div>
          ) : (
            <div className="jl-status">
              <div className="jl-status__header">
                <div>
                  <h3 className="jl-status__header-title">Riwayat Lamaran</h3>
                  <p className="jl-status__count">
                    Menampilkan <strong>{filteredApplications.length}</strong> dari <strong>{myApplications.length}</strong> lamaran
                  </p>
                </div>
              </div>

              <div className="jl-status__table-wrap">
                <div className="jl-status__filters">
                  <div className="jl-status-filter jl-status-filter--search">
                    <label>Cari Lamaran</label>
                    <input
                      type="search"
                      value={applicationSearch}
                      placeholder="Cari posisi atau perusahaan..."
                      onChange={(event) => setApplicationSearch(event.target.value)}
                    />
                  </div>

                  <div className="jl-status-filter">
                    <label>Status Interview</label>
                    <select
                      value={applicationInterviewFilter}
                      onChange={(event) => setApplicationInterviewFilter(event.target.value as InterviewStatusFilter)}
                    >
                      <option value="all">Semua</option>
                      <option value="not_started">Belum Interview</option>
                      <option value="in_progress">Sedang Interview</option>
                      <option value="technical_interrupted">Terkendala Teknis</option>
                      <option value="completed">Selesai Interview</option>
                    </select>
                  </div>

                  <div className="jl-status-filter">
                    <label>Status Lamaran</label>
                    <select
                      value={applicationStatusFilter}
                      onChange={(event) => setApplicationStatusFilter(event.target.value)}
                    >
                      <option value="all">Semua</option>
                      {applicationStatusOptions.map((statusName) => (
                        <option key={String(statusName)} value={String(statusName)}>
                          {String(statusName)}
                        </option>
                      ))}
                    </select>
                  </div>

                  {hasAdditionalFilters && (
                    <button
                      type="button"
                      className="jl-status__reset-filter"
                      onClick={resetApplicationFilters}
                    >
                      <FiRefreshCw />
                      <span>Reset Filter</span>
                    </button>
                  )}
                </div>

                {filteredApplications.length === 0 ? (
                  <div className="jl-status__guest jl-status__guest--filtered">
                    <div className="jl-status__guest-icon">
                      <FiSearch />
                    </div>
                    <h3>Tidak ada lamaran sesuai filter</h3>
                    <p>Coba ubah status interview, status lamaran, atau kata pencarian.</p>
                  </div>
                ) : (
                  <div className="jl-app-cards">
                    {filteredApplications.map((application: any) => {
                      const meta = statusMeta(application.status?.ms_status_name);
                      const interview = getApplicationInterview(application);
                      const interviewScore = interview?.result?.final_score;
                      const interviewStatus = interview?.result?.status;
                      const interviewTimestamp = resolveInterviewTimestampMeta(interview);
                      const interviewFlowState = getInterviewFlowState(interview);
                      const applicationStatusName = application.status?.ms_status_name || '';
                      const showApplicationStatus =
                        applicationStatusName.trim().toLowerCase() !== 'new registrant' && applicationStatusName.trim().length > 0;

                      const interviewStatusLabel: Record<string, string> = {
                        strong: 'Sangat Baik',
                        average: 'Cukup Baik',
                        weak: 'Perlu Peningkatan',
                      };

                      return (
                        <article key={application.application_id || `${application.outsource_id}-${application.job_id}`} className="jl-app-card">
                          <div className="jl-app-card__header">
                            <div className="jl-app-card__title-group">
                              <div className="jl-app-card__headline">
                                <h3 className="jl-app-card__title">
                                  {application.job?.title || application.position || 'Lamaran'}
                                </h3>
                                {showApplicationStatus && (
                                  <span className={`jl-status__badge jl-status__badge--${meta.tone}`}>
                                    {meta.icon}
                                    <span>{meta.label}</span>
                                  </span>
                                )}
                              </div>
                              <div className="jl-app-card__meta-row">
                                {application.job?.company?.company_name || application.job?.company?.name ? (
                                  <span className="jl-app-card__meta-item">
                                    <FiFileText />
                                    <span>{application.job?.company?.company_name || application.job?.company?.name}</span>
                                  </span>
                                ) : null}
                                <span className="jl-app-card__meta-item">
                                  <FiCalendar />
                                  <span>
                                    {application.created_at
                                      ? new Date(application.created_at).toLocaleDateString('id-ID', {
                                          day: 'numeric',
                                          month: 'short',
                                          year: 'numeric',
                                        })
                                      : '-'}
                                  </span>
                                </span>
                              </div>
                            </div>

                            {application.job?.id && (
                              <div className="jl-app-card__side">
                                <span className={`jl-iv-flow jl-iv-flow--${interviewFlowState}`}>
                                  {interviewFlowState === 'not_started' && (
                                    <>
                                      <FiClock />
                                      <span>Belum Interview</span>
                                    </>
                                  )}
                                  {interviewFlowState === 'in_progress' && (
                                    <>
                                      <FiPlayCircle />
                                      <span>Sedang Interview</span>
                                    </>
                                  )}
                                  {interviewFlowState === 'completed' && (
                                    <>
                                      <FiCheckCircle />
                                      <span>Selesai Interview</span>
                                    </>
                                  )}
                                  {interviewFlowState === 'technical_interrupted' && (
                                    <>
                                      <FiAlertCircle />
                                      <span>Terkendala Teknis</span>
                                    </>
                                  )}
                                </span>

                                {interviewTimestamp ? (
                                  <span className="jl-app-card__ai-meta-row">
                                    {interviewTimestamp.label}: {interviewTimestamp.display}
                                  </span>
                                ) : null}

                                {interviewFlowState === 'completed' && interviewStatus && (
                                  <div className="jl-iv-result__score-wrap">
                                    <span className="jl-iv-result__score">{interviewScore ?? '-'} / 100</span>
                                    <span className="jl-iv-result__badge">
                                      {interviewStatusLabel[interviewStatus] ?? interviewStatus}
                                    </span>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>

                          {application.job?.id && (
                            <div className="jl-app-card__ai">
                              <div className="jl-app-card__ai-cta">
                                {interviewFlowState === 'not_started' && (
                                  <button
                                    className="jl-iv-btn jl-iv-btn--start"
                                    onClick={() =>
                                      navigate(`/interview/${application.job.id}`, {
                                        state: {
                                          jobId: application.job.id,
                                          outsourceId: application.outsource_id,
                                          jobTitle: application.job?.title || application.position || 'Lowongan',
                                        },
                                      })
                                    }
                                  >
                                    <FiPlayCircle />
                                    <span>Mulai Interview</span>
                                  </button>
                                )}

                                {interviewFlowState === 'in_progress' && (
                                  <button
                                    className="jl-iv-btn jl-iv-btn--resume"
                                    onClick={() =>
                                      navigate(`/interview/session/${application.job.id}`, {
                                        state: {
                                          interviewId: interview.id,
                                          jobId: application.job.id,
                                          outsourceId: application.outsource_id,
                                          sessionMode: 'resume',
                                        },
                                      })
                                    }
                                  >
                                    <FiRefreshCw />
                                    <span>Lanjutkan Interview</span>
                                  </button>
                                )}

                                {interviewFlowState === 'technical_interrupted' && (
                                  <button
                                    className="jl-iv-btn jl-iv-btn--start"
                                    onClick={() =>
                                      navigate(`/interview/${application.job.id}`, {
                                        state: {
                                          jobId: application.job.id,
                                          outsourceId: application.outsource_id,
                                          jobTitle: application.job?.title || application.position || 'Lowongan',
                                        },
                                      })
                                    }
                                  >
                                    <FiPlayCircle />
                                    <span>Mulai Interview Lagi</span>
                                  </button>
                                )}

                                {interviewFlowState === 'completed' && interviewStatus && (
                                  <button
                                    className="jl-iv-btn jl-iv-btn--detail"
                                    onClick={() =>
                                      navigate(`/interview/detail/${application.job.id}`, {
                                        state: { interviewId: interview.id },
                                      })
                                    }
                                  >
                                    <span>Lihat Detail</span>
                                    <FiArrowRight />
                                  </button>
                                )}
                              </div>
                            </div>
                          )}
                        </article>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StatusLamaran;
